import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClaimAssigneeOption,
  ClaimRotation,
  SetClaimRotations,
} from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';

/** O rodízio de responsáveis (20/10/2026) — a configuração, por departamento. */

const ROOT = ['sinistros', 'rodizios'] as const;

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export function useRotations() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, tenantId],
    queryFn: async (): Promise<ClaimRotation[]> => {
      const r = await api.claims.listRotations();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os rodízios'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

export function useSetRotations() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetClaimRotations) =>
      ok(
        await api.claims.setRotations({ body }),
        200,
        'Não foi possível gravar os rodízios.',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: [...ROOT] }),
  });
}

/** Quem pode entrar num rodízio: as pessoas ativas da base — a lista do responsável. */
export function useRotationPeople() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'pessoas', tenantId],
    queryFn: async (): Promise<ClaimAssigneeOption[]> => {
      const r = await api.claims.listRotationAssigneeOptions();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as pessoas'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    staleTime: 5 * 60_000,
  });
}
